import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import SearchPanel from "@widgets/SearchPanel"; // 조회조건 Panel
import { fnSave, fnGetDataSet } from "@shared/api/restApi"; // BackEnd API
import { getInitFormData } from "@shared/utils/CommonUtils"; // 공통 유틸 메뉴 초기화(조회조건, 그리드, 프리폼) 설정 load 관련 내용
import { showDialog } from "@widgets/CommonDialog"; // Message Box
import { global } from "@shared/libs/Global"; // Login User관련 전역 변수( 로그인 정보)
import SnGrid from "@shared/components/GridV2/SnGrid";
import { menuInfo } from "@shared/type/CommonType"; // 공통 Interface
import SnTab from "@shared/components/SnTab";
import SnTabHeader from "@shared/components/SnTabHeader";
import SnHeader from "@shared/components/SnHeader";

interface Props {
  Menuprops: menuInfo;
}

const SN_COS_MAS_REG_012 = forwardRef(({ Menuprops }: Props, ref) => {
  //#region  variable
  const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
    grid1: React.createRef(), // 구매품평가
    grid2: React.createRef(), // 원단원가집계(FAB)
    grid3: React.createRef(), // 제품원가집계(PRD)
    grid4: React.createRef(), // 재고평가
  });
  const searchRef = useRef<any>(null);
  const today = new Date();
  const todayform = `${today.getFullYear()}${(today.getMonth() + 1)
    .toString()
    .padStart(2, "0")}${today.getDate().toString().padStart(2, "0")}`;
  const { userInfo } = global(); // Login user 정보
  //#endregion

  //#region  Components useState
  const [formData, setFormData] = useState<any>(); // 조회조건 변수
  const [formControlData, setFormControlData] = useState<any[]>([]); // 조회조건 설정된 항목 binding
  const [columns, setColums] = useState<any>({
    grid1: [],
    grid2: [],
    grid3: [],
    grid4: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
    grid2: [],
    grid3: [],
    grid4: [],
  });
  // 상단 버튼(원가집계) 메타 — sys_headerctrl(cd_form=grid1~4)
  const [headerInfo, setHeaderInfo] = useState<any>({});

  //#endregion

  //#region grid 높이 관련

  //#endregion

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();

        setFormControlData(rtn.formControlData); // 조회조건 항목 설정
        setFormData(rtn.formData); // 조회조건 초기값
        setColums({
          grid1: rtn.grid.grid1,
          grid2: rtn.grid.grid2,
          grid3: rtn.grid.grid3,
          grid4: rtn.grid.grid4,
        });
        setHeaderInfo(rtn.header ?? {});
      } catch (error) {}
    };
    fetchform();
  }, []);

  //#region  Button Event
  useImperativeHandle(ref, () => ({
    onSearch,
    onDelete,
  }));

  const onSearch = async () => {
    try {
      const params = {
        id: "sn_cos_mas_reg_012_01",
        params: searchRef.current.getFormdata(),
      };

      const data: any = await fnGetDataSet(params);
      setDataSources({
        grid1: data[0] || [], // 구매품평가
        grid2: data[1] || [], // 원단원가집계(FAB)
        grid3: data[2] || [], // 제품원가집계(PRD)
        grid4: data[3] || [], // 재고평가
      });

      gridRefs.current["grid1"].current?.resetUpdate();
      gridRefs.current["grid2"].current?.resetUpdate();
      gridRefs.current["grid3"].current?.resetUpdate();
      gridRefs.current["grid4"].current?.resetUpdate();
    } catch (error: any) {}
  };

  const onDelete = async () => {
    const hasData = Object.values(dataSources).some(
      (rows: any) => Array.isArray(rows) && rows.length > 0
    );
    if (!hasData) {
      await showDialog("alter", "삭제할 데이터가 없습니다.");
      return;
    }

    const searchData = searchRef.current?.getFormdata?.() ?? {};
    const ymFrom = String(searchData.dt_from ?? "")
      .replace(/\D/g, "")
      .substring(0, 6);
    const ymTo = String(searchData.dt_to ?? "")
      .replace(/\D/g, "")
      .substring(0, 6);
    const params = {
      cd_firm: String(userInfo.cd_firm).trim(),
      cd_biz: String(searchData.cd_biz ?? "").trim(),
      from: ymFrom,
      to: ymTo,
    };

    if (
      !params.cd_firm ||
      !params.cd_biz ||
      params.from.length !== 6 ||
      params.to.length !== 6 ||
      params.from > params.to
    ) {
      await showDialog(
        "alter",
        "회사, 사업장, 조회기간을 확인해주시기 바랍니다."
      );
      return;
    }

    const confirm = await showDialog("confirm", "데이터를 삭제하시겠습니까?");
    if (!confirm) return;

    try {
      const ok = await fnSave("SN_COS_MAS_REG_012_DELETE_01", params);
      if (ok) {
        await onSearch();
      }
    } catch (error: any) {}
  };
  //#endregion

  const onCellValueChangedH = async (event: any) => {
    if (!event) return;
  };

  // 상단 원가집계 버튼
  //  - 구매품평가 탭(btnEvaluation): fn_cos_all_tex 실행(데이터 생성) 후 재조회
  //  - 그 외 탭(btnAggregate): 자동 조회만(집계는 구매품평가 버튼/외부에서 수행)
  const onGridBtnsClick = async (id: string) => {
    if (id === "btnAggregate") {
      await onSearch();
      await showDialog("alter", "원가집계가 완료되었습니다.");
    } else if (id === "btnEvaluation") {
      const formData = searchRef.current.getFormdata();
      const params = {
        cd_firm: userInfo.cd_firm,
        cd_biz: formData.cd_biz,
        ym_start: formData.dt_from?.toString().substring(0, 6),
        ym_end: formData.dt_to?.toString().substring(0, 6),
        cd_emp: userInfo.cd_emp,
      };
      const ok = await fnSave("SN_COS_MAS_REG_012_Btn", params);
      if (ok) {
        await onSearch();
      }
    }
  };

  //#endregion

  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
        events: {},
      },
      grid2: {
        columns: columns.grid2,
        dataSource: dataSources.grid2,
        events: {},
      },
      grid3: {
        columns: columns.grid3,
        dataSource: dataSources.grid3,
        events: {},
      },
      grid4: {
        columns: columns.grid4,
        dataSource: dataSources.grid4,
        events: {},
      },
    }),
    [columns, dataSources]
  );

  //#region  UI
  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* 검색 */}
      <div className="mb-1 shrink-0">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
        />
      </div>

      {/* 탭영역 */}
      <div className="flex-1 min-h-0">
        <SnTab
          titles={[
            "구매품평가",
            "원단원가집계",
            "제품원가집계",
            "재고평가",
          ]}
          width="100%"
          header={[
            <SnHeader
              headerInfo={headerInfo?.grid1 ?? { controls: [], data: {} }}
              onButtonClick={onGridBtnsClick}
            />,
            <SnHeader
              headerInfo={headerInfo?.grid2 ?? { controls: [], data: {} }}
              onButtonClick={onGridBtnsClick}
            />,
            <SnHeader
              headerInfo={headerInfo?.grid3 ?? { controls: [], data: {} }}
              onButtonClick={onGridBtnsClick}
            />,
            <SnHeader
              headerInfo={headerInfo?.grid4 ?? { controls: [], data: {} }}
              onButtonClick={onGridBtnsClick}
            />,
          ]}
          body={[
            // TAB1 구매품평가
            <div className="h-full min-h-0">
              <SnGrid
                ref={gridRefs.current["grid1"]}
                columnConfig={grids.grid1.columns}
                rowData={grids.grid1.dataSource}
              />
            </div>,

            // TAB2 원단원가집계(FAB)
            <div className="h-full min-h-0">
              <SnGrid
                ref={gridRefs.current["grid2"]}
                columnConfig={grids.grid2.columns}
                rowData={grids.grid2.dataSource}
              />
            </div>,

            // TAB3 제품원가집계(PRD)
            <div className="h-full min-h-0">
              <SnGrid
                ref={gridRefs.current["grid3"]}
                columnConfig={grids.grid3.columns}
                rowData={grids.grid3.dataSource}
              />
            </div>,

            // TAB4 재고평가
            <div className="h-full min-h-0">
              <SnGrid
                ref={gridRefs.current["grid4"]}
                columnConfig={grids.grid4.columns}
                rowData={grids.grid4.dataSource}
              />
            </div>,
          ]}
        />
      </div>
    </div>
  );
  //#endregion
});

export default SN_COS_MAS_REG_012;
